import "./SpaceTrack.scss";
import React, { useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { CarouselSyncProvider } from "App/routes/Main/Space/Carousel/useCarouselSync";
import Carousel from "./Carousel/Carousel";
import { useCommentCards } from "App/common/useCommentCards";
import { useSpaceContext } from "App/common/useSpace";
import { THEME, ThemeProvider } from "App/common/ThemeProvider";
import { SpaceCommentsContextProvider } from "App/common/useSpaceComments";
import { AdsContextProvider } from "App/common/Ads/AdsContext";
import { TrackWaveform } from "./TrackWaveform";
import {
  checkIsUserAnonymous,
  getDefaultVersionId,
  getEntitySubscribers,
  getNextInArray,
  getPrevInArray,
  hasRole,
  spaceHasRolesCheck,
} from "@highnote/server/src/core/shared-util";
import { CommentFilters } from "App/components/SpaceTools";
import { useHistory, useLocation } from "react-router";
import { EntityNotFound } from "../EntityNotFound";
import { useTrack } from "App/components/useEntities/useTrack";
import { useUrlContext } from "../useUrlContext";
import { useGlobalAudioPlayer } from "App/components/GlobalAudioPlayer";
import { useTrackEditor } from "App/components/useTrackEditor";
import {
  GlobalAudioPlayer,
  GlobalAudioTrackPreview,
} from "App/components/GlobalAudioPlayer/GlobalAudioPlayer";
import { useViewport } from "App/common/useViewport";
import { daw, useDAWTrack } from "@highnote/daw/src";
import { SpaceTrackNavigation } from "./SpaceNavigation";
import {
  FocusedCardContextProvider,
  useFocusedCard,
} from "App/common/useFocusedCard";
import { TrackVersionTabs } from "App/routes/Main/Space/TrackVersionTabs";
import { EditableArtwork } from "App/components/EditableArtwork";
import { useNotificationsContext } from "App/common/useNotifications";
import {
  COLLECTION_ID,
  EVENT_ID,
  KNOCK_WORKFLOW,
  ROLE,
} from "@highnote/server/src/core/entities";
import { useEntityShareKeyInUrl } from "App/components/ShareDialog/Members/InviteByLink";
import { useChildEntityWatchers } from "App/store/helpers/useChildEntityWatchers";
import { useGlobalTracks } from "App/store/tracks/useGlobalTracks";
import { useSpaceTrackRowsLoading } from "App/store/spaceTracks/useSpaceTrackRowsLoading";
import { DownloadRequestsContextProvider } from "App/components/useEntities/useDownloadRequests";
import {
  getSortedChildRows,
  useFormatTrackToBaseEntityRow,
} from "App/components/EntityTable/ExpandableSpace/ExpandableSpaceRow";
import { SORT_TYPE } from "../Library/config";
import { useIntercomContext } from "App/services/intercom/IntercomContext";
import { useMoveToSpace } from "App/components/useEntities/useLibraryTracks/useMoveToSpace";
import { useMobileAppParams } from "../useMobileAppParams";
import { useSegmentContext } from "App/modules/useSegment";
import { ReactComponent as FilterSVG } from "App/common/icons-v2/filter.svg";
import { useUser } from "App/components/useEntities/useUser";
import { ButtonV2 } from "App/core/ButtonV2";
import { SPACE_TRACKS_WATCHER_COMPONENT_ID } from "App/store/watchers/useWatcherV2";
import {
  TRACK_VERSION_EDITOR_WATCHER_COMPONENT_ID,
  useTrackVersionEditorContext,
} from "App/components/TrackVersionEditor/utils/context/TrackVersionEditorContext";
import { TrackCommentModule } from "./TrackCommentModule/TrackCommentModule";

export const useDAWTrackVersionId = () => {
  const { currentTrackVersion, track } = useTrack();
  const defaultVersionId = getDefaultVersionId(track);
  return currentTrackVersion?.id === defaultVersionId
    ? track?.id
    : currentTrackVersion?.id;
};

export const useTrackVersionInDAW = () => {
  const dawTrackId = useDAWTrackVersionId();
  const dawTrack = useDAWTrack(dawTrackId);
  return dawTrack;
};

const SpaceTrackArtwork = () => {
  const { spaceArtworkUrl } = useSpaceContext();
  const { trackArtworkUrl } = useTrack();

  return (
    <div className="track-art">
      <EditableArtwork
        disabled
        artworkUrl={trackArtworkUrl}
        fallbackArtworkUrl={
          spaceArtworkUrl || "/public/default-track-artwork.png"
        }
      />
    </div>
  );
};

const getSpaceTrackToolsEl = () =>
  document.getElementById("TEMP_SPACE_TRACK_TOOLS");

const SpaceTrackUI = () => {
  const { user } = useUser();
  const { versionIdPath, viewAsShareKeyUser } = useUrlContext();
  const { pathname } = useLocation();
  const { focusedCardId } = useFocusedCard();
  const { openTrackVersionEditor } = useTrackVersionEditorContext();
  const { isMaxViewportL, isMobile } = useViewport();
  const { pause, setQueue, queueTrackVersion, preloadTrackVersions } =
    useGlobalAudioPlayer();
  const {
    track,
    currentTrackVersion,
    pinnedTrackVersions,
    trackArtworkUrl,
    isBorrowed,
  } = useTrack();
  const { space, spaceId, spaceArtworkUrl } = useSpaceContext();
  const { notifications, markAsRead } = useNotificationsContext();
  const { orderedCards } = useCommentCards();
  const waveformRef = useRef<HTMLDivElement>();
  const hasComments = orderedCards.length > 0;
  const unmountedRef = useRef<boolean>(false);

  // Get all the valid share keys (must have roles attached).
  const checkIfSpaceHasCommentsOn = () =>
    spaceHasRolesCheck(space, [ROLE.COMMENT]);

  const subscribers = getEntitySubscribers(space);
  const isUserMember = subscribers.includes(user?.id);
  const canUserComment = viewAsShareKeyUser
    ? spaceHasRolesCheck(space, [ROLE.COMMENT])
    : hasRole(user?.id, ROLE.COMMENT, space);

  const canUserUpload = viewAsShareKeyUser
    ? spaceHasRolesCheck(space, [ROLE.UPLOAD])
    : hasRole(user?.id, ROLE.UPLOAD, space);

  const showCommentToolbar = isUserMember
    ? canUserComment
    : checkIfSpaceHasCommentsOn();

  const isUserAnonymous = checkIsUserAnonymous(user?.id);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  // Reset the minimum duration prop when you leave Space Track
  useEffect(() => {
    if (!track?.id) return;

    // Special case: if the track specified on this page was ALREADY active
    // before you arrived here, retain the time and play state.
    // Otherwise PAUSE audio and start at 0.
    const dawTrack = daw.getTrack(track.id);
    const trackTime = daw.getTrackTime(track.id);
    const alreadyPlaying = trackTime > 0 && trackTime < dawTrack?.duration;
    if (!alreadyPlaying) {
      pause();
      daw.seekTo(dawTrack?.startTime || 0);
    }

    return () => {
      daw.__minDuration = 0;
    };
  }, [!!track?.id]);

  useEffect(() => {
    if (!track?.id) {
      return;
    }
    if ((track.versionFilesV2 || []).length < 1) {
      setQueue({
        type: "UPDATE_CURRENT",
        _tracks: [],
        keepPreloads: true,
        currentPageId: pathname,
      });
      return;
    }
    setQueue({
      type: "UPDATE_CURRENT",
      // Stamp the VIEWING space onto the queued track. Reading the route
      // HERE is correct and is the entire point: the hazard PR #1642 avoided
      // is reading the route at FETCH time, because the queue outlives the
      // route it was built from — enqueue a borrowed track on
      // /space/BORROWER/T, navigate to /library, and it keeps playing while
      // the URL's spaceId goes undefined. Capturing it at enqueue time is
      // what makes the fetch-time read unnecessary.
      //
      // Tiles built by the space-page pipeline already carry this
      // (buildSpaceTrackTile sets it unconditionally); the track page
      // enqueues the canonical doc from useTrack(), which does not.
      _tracks: [{ ...track, viewingSpaceId: spaceId }],
      keepPreloads: true,
      currentPageId: pathname,
    });
    preloadTrackVersions(track.id, pinnedTrackVersions);
    // While track files refresh, currentTrackVersion is briefly undefined.
    // Never push that into the queue: an undefined active version makes the
    // player mute/remove the version that is currently playing.
    if (currentTrackVersion?.id) {
      queueTrackVersion(track.id, currentTrackVersion.id);
      daw.__minDuration = currentTrackVersion.metadata?.duration || 0;
    }
  }, [currentTrackVersion, track?.id]);

  useEffect(() => {
    if (!track?.id) return;
    const unreads = notifications.filter((n) => {
      // Track ID must match
      const isTrackAddedNotif =
        n.source.key === KNOCK_WORKFLOW.TRACK_ADDED &&
        n.data.trackId === track.id;

      // Version ID must match & be pinned
      const isTrackVersionAdded =
        n.source.key === KNOCK_WORKFLOW.TRACK_VERSION_ADDED &&
        pinnedTrackVersions.includes(n.data.versionId);

      return (isTrackAddedNotif || isTrackVersionAdded) && !n.read_at;
    });
    markAsRead(unreads);
  }, [track?.id, pinnedTrackVersions, notifications]);

  const trackVersionsLength = track?.versionFilesV2?.length;

  return (
    <ThemeProvider theme={THEME.DARK}>
      {getSpaceTrackToolsEl() &&
        isMaxViewportL &&
        ReactDOM.createPortal(
          <div className="highnote-comment-tools">
            <CommentFilters
              SelectProps={{
                endAdornment: <FilterSVG />,
                size: "xs",
              }}
            />
          </div>,
          getSpaceTrackToolsEl(),
        )}

      <div className="ThreadUI">
        <section className="thread-comments" data-has-comments={hasComments}>
          <SpaceTrackNavigation />

          <div className="space-comments">
            <div className="space-track-header">
              {(currentTrackVersion?.name || track?.title) && (
                <h1 className="space-track-header-title">
                  {versionIdPath ? currentTrackVersion?.name : track?.title}
                </h1>
              )}

              {(canUserUpload || showCommentToolbar) && (
                <div className="space-track-header-actions">
                  {canUserUpload && (
                    <ButtonV2
                      size="xs"
                      color="secondary"
                      onClick={() =>
                        openTrackVersionEditor({
                          track,
                          isBorrowed,
                          type: "default",
                          viewingSpaceId: spaceId,
                        })
                      }
                    >
                      {trackVersionsLength
                        ? `${trackVersionsLength} ${trackVersionsLength > 1 ? "versions" : "version"}`
                        : "Versions"}
                    </ButtonV2>
                  )}

                  {showCommentToolbar && (
                    <CommentFilters
                      SelectProps={{
                        endAdornment: <FilterSVG />,
                        size: "xs",
                      }}
                    />
                  )}
                </div>
              )}
            </div>

            <div className="carousel-container">
              <div className="track-art-backdrop-wrapper">
                <span className="track-art-backdrop">
                  <EditableArtwork
                    disabled
                    artworkUrl={trackArtworkUrl}
                    fallbackArtworkUrl={
                      spaceArtworkUrl || "/public/default-track-artwork.png"
                    }
                    blurredImage={isMobile ? "small" : "large"}
                    borderRadius="round"
                  />
                </span>
              </div>
              <Carousel />
            </div>

            {showCommentToolbar && (
              <div className="space-track-tools">
                <section
                  className="comment-toolbar"
                  data-is-focused={!!focusedCardId}
                >
                  <div className="inner">
                    <div className="middle-col">
                      <TrackCommentModule targetEl={waveformRef.current} />
                      {!isUserAnonymous && (
                        <CommentFilters
                          SelectProps={{
                            className: "highnote-filters-screen-height-min",
                            removeIconPadding: true,
                            color: "secondary",
                            size: "md",
                            shape: "circle",
                            endAdornment: <></>, // Empty adornment to prevent the default adornment from appearing
                            renderValue: () => (
                              <span aria-label="Filter Comments">
                                <FilterSVG />
                              </span>
                            ),
                          }}
                        />
                      )}
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>

          <div ref={waveformRef} className="space-track-waveform">
            <TrackWaveform />
          </div>
          {!versionIdPath && <TrackVersionTabs />}
        </section>
      </div>
    </ThemeProvider>
  );
};

const SpaceTrackPlayer = () => {
  const history = useHistory();
  const { spaceId, space, itemSortType } = useSpaceContext();
  const { formatTracksListToBaseEntityRows } = useFormatTrackToBaseEntityRow();
  const { getChildTracks } = useChildEntityWatchers();
  const { globalTracks } = useGlobalTracks();
  const spaceTracks = useMemo(() => {
    const formattedChildTracks = formatTracksListToBaseEntityRows({
      tracks: getChildTracks(spaceId),
      viewingSpaceId: spaceId,
    });

    // Sort the child tracks by the space's itemSortType
    return getSortedChildRows({
      childItemRows: formattedChildTracks,
      sortType: itemSortType || SORT_TYPE.CUSTOM,
      itemsOrder: space?.itemsOrder || [],
    });
  }, [
    spaceId,
    itemSortType,
    space?.itemsOrder,
    globalTracks.childTracks.get(spaceId),
  ]);

  const { track } = useTrack();
  const { openTrackEditor } = useTrackEditor();

  const spaceTrack = spaceTracks.find((s) => s.id === track?.id);
  const nextSpaceTrack = getNextInArray(spaceTrack, spaceTracks);
  const prevSpaceTrack = getPrevInArray(spaceTrack, spaceTracks);

  return (
    <GlobalAudioPlayer
      seekable={false}
      preview={
        <>
          <GlobalAudioTrackPreview
            track={track}
            onClick={() => {
              openTrackEditor({ track, viewingSpaceId: spaceId });
            }}
          ></GlobalAudioTrackPreview>
        </>
      }
      onNext={
        nextSpaceTrack
          ? () => {
              history.push(`/space/${spaceId}/${nextSpaceTrack.id}`);
            }
          : undefined
      }
      onPrev={
        prevSpaceTrack
          ? () => {
              history.push(`/space/${spaceId}/${prevSpaceTrack.id}`);
            }
          : undefined
      }
    />
  );
};

export const SpaceTrack = () => {
  const { spaceId, space } = useSpaceContext();
  const { openMoveToSpace } = useMoveToSpace();
  const { trackId } = useUrlContext();
  const { showMoveToSpaceDialog } = useMobileAppParams();
  const { setShowLauncher } = useIntercomContext();
  const { setTrackId } = useTrackVersionEditorContext();
  const { trackEvent } = useSegmentContext();

  const { globalTracks, globalTracksWatchers } = useGlobalTracks();
  const { track: trackFromDoc, isBorrowed } = useTrack();
  const isChildTrack = globalTracks.childTracks.get(spaceId)?.includes(trackId);
  // `globalTracks.tracks` is populated for this id by only two things: the
  // legacy `tracks-v2 where spaceId == S` space query (ADD_CHILD_TRACKS),
  // which by definition never returns a BORROWED track, and the
  // track-version-editor's own watcher (SET_TRACK) — which only attaches
  // once the `setTrackId(track.id)` effect below has fired, and that needs
  // `track`. For a borrowed track those two form a closed cycle: `track`
  // stays undefined forever, so the watcher never attaches, so `track`
  // stays undefined.
  //
  // `useTrack()` breaks it. `Space/index.tsx` already mounts
  // `TrackContextProvider` keyed off the URL's trackId. For a track in its
  // OWN space that provider does a real onSnapshot on
  // `tracks-v2/{trackId}`; for a BORROWED one it builds the track from the
  // `space-tracks-v1` row instead and never touches the canonical document,
  // which the rules now keep origin-only. Either way it is the same object
  // `SpaceTrackUI` (and the rest of this file) already renders, so this
  // reads one source instead of two disagreeing ones.
  //
  // The store is kept as the fallback deliberately: it is populated for a
  // legacy track by the space query before the single-doc snapshot resolves,
  // so preferring the doc alone would open a window where both the store
  // read is skipped and the doc has not arrived — a flash of
  // `EntityNotFound` on a track that is perfectly fine.
  const track = trackFromDoc || globalTracks.tracks.get(trackId);
  // The listener that loads this page's track is the SPACE's child-tracks
  // watcher (attached by useSpace.tsx under SPACE_TRACKS_WATCHER_COMPONENT_ID
  // as soon as the space loads). Until it has answered once — no entry yet,
  // or its loading flag still set — the track is merely not here YET, not
  // missing. The single-track watcher only attaches after `setTrackId`,
  // i.e. after the track already exists, so on its own it can never say
  // "loading" in the not-found branch below.
  // A borrowed track is carried by the space-tracks-v1 rows, not by the
  // legacy space query, so its watcher gates the not-found screen too.
  const rowsLoading = useSpaceTrackRowsLoading(spaceId);
  const spaceTracksWatcher = globalTracksWatchers.get(spaceId);
  const trackIsLoading =
    rowsLoading ||
    !spaceTracksWatcher ||
    spaceTracksWatcher.loading?.get(SPACE_TRACKS_WATCHER_COMPONENT_ID) ||
    globalTracksWatchers
      .get(trackId)
      ?.loading?.get(TRACK_VERSION_EDITOR_WATCHER_COMPONENT_ID);
  const lastViewedTrackRef = useRef(track);

  useEntityShareKeyInUrl(space);

  useEffect(() => {
    setShowLauncher(false);
  }, []);

  useEffect(() => {
    if (!track) {
      lastViewedTrackRef.current = undefined;
      return;
    }

    if (track.id === lastViewedTrackRef.current?.id) {
      return;
    }

    trackEvent(EVENT_ID.ENTERED_TRACK, {
      track_id: track.id,
      track_title: track.title,
      space_id: spaceId,
    });
    lastViewedTrackRef.current = track;
  }, [track]);

  useEffect(() => {
    if (!trackId) return;

    return () => {
      trackEvent(EVENT_ID.EXITED_TRACK, {
        track_id: trackId,
      });
    };
  }, [trackId]);

  useEffect(() => {
    if (showMoveToSpaceDialog && track) {
      openMoveToSpace({
        entity: track,
        entityType: COLLECTION_ID.TRACK,
      });
    }
  }, [showMoveToSpaceDialog, track]);

  useEffect(() => {
    if (!track?.id) return;

    // The version editor watches the canonical document. A borrowed track
    // has none this viewer can read; its versions come from the row. Clear
    // the editor rather than leaving it: an id kept from a previously
    // viewed canonical track holds that document's watcher open behind a
    // page that no longer shows it. A falsy id is the context's own reset
    // path (see TrackVersionEditorContext's `trackId` effect).
    if (isBorrowed) {
      setTrackId(undefined);
      return;
    }

    // The track page is scoped to the route's space.
    setTrackId(track.id, spaceId);
  }, [track?.id, spaceId, isBorrowed]);

  if (!track && !isChildTrack) {
    return (
      <>
        <SpaceTrackNavigation />
        {!trackIsLoading && <EntityNotFound entityType="track" />}
      </>
    );
  }

  return (
    <SpaceCommentsContextProvider spaceId={spaceId} trackId={trackId}>
      <AdsContextProvider spaceId={spaceId}>
        <CarouselSyncProvider
          addCard={{
            insertAt: "first",
            component: () => <SpaceTrackArtwork />,
          }}
        >
          <FocusedCardContextProvider>
            <DownloadRequestsContextProvider isProcessing>
              <SpaceTrackUI />
            </DownloadRequestsContextProvider>
            <SpaceTrackPlayer />
          </FocusedCardContextProvider>
        </CarouselSyncProvider>
      </AdsContextProvider>
    </SpaceCommentsContextProvider>
  );
};
